(() => {
  /**
   * Handle the centre symbol devil character spine animations
   */
  class CentreDevil extends PIXI.Container {
    constructor() {
      super();
      // asset name
      this._devilSpineName = "KrakenSpine";
      this._symbolMap = null;
      this._matchSmb = null;

      // devil spine asset
      this._devilCharacterSpine = null;

      this._standardBGMovie = null;
      this._boostBGMovie = null;
      this._multiBGMovie = null;
      this._respinBGMovie = null;

      this._devilScale = 0.34;

      this._coinFlipSoundDelay = 1.1;

      this._currentModiferBackgroundShowing = null;

      this._cycleBackgroundIndex = 0;
      this._cycleOutTween = null;
      this._cycleInTween = null;
      this._cycleInLandingSymbol = false;
      this._cycleInNextBackground = null;

      this._idleLoopCounter = 0;

      Reflex.registerEventHandler("orientationChange", this._orientationChange.bind(this));
    }

    /**
     * Initialise the class
     */
    initialise() {
      this._devilContainer = resourceManager.get("slotContainer:centreDevil:CentreDevilContainer");
      this.createBackgroundSprites();

      this._blackSquare = resourceManager.createObject("graphics", "blackSquare", this._devilContainer);
      this._blackSquare.beginFill(0x000000).drawRect(0, 0, 1, 1).endFill();
      this._blackSquare.width = 100;
      this._blackSquare.height = 34;
      this._blackSquare.pivot.x = 0.5;
      this._blackSquare.pivot.y = 0.5;
      this._blackSquare.y = -119;
      this._blackSquare.alpha = 0;

      this._devilCharacterSpine = resourceManager.createObject(
        "spine",
        this._devilSpineName + "Center",
        this._devilContainer,
        {
          name: this._devilSpineName,
          defaultAnimation: "idle",
          scaleX: this._devilScale,
          scaleY: this._devilScale,
          x: 0,
          y: -136,
        }
      );

      this._coinScale = 0.06;
      this._coinLeftPos = [
        { x: -120, y: -110 },
        { x: -320, y: -500 },
      ];
      this._coinRightPos = [
        { x: 128, y: -110 },
        { x: 340, y: -500 },
      ];

      this._coinLeft = resourceManager.createObject("movie", "coinLeft", this._devilContainer, {
        definition: "coinFlip",
        x: this._coinLeftPos[0].x,
        y: this._coinLeftPos[0].y,
        anchor: 0.5,
        scale: this._coinScale,
        loop: true,
      });
      this._coinLeft.gotoAndPlay(0);
      this._coinLeft.visible = false;

      this._coinRight = resourceManager.createObject("movie", "coinRight", this._devilContainer, {
        definition: "coinFlip",
        x: this._coinRightPos[0].x,
        y: this._coinRightPos[0].y,
        anchor: 0.5,
        scale: this._coinScale,
        loop: true,
      });
      this._coinRight.gotoAndPlay(0);
      this._coinRight.visible = false;

      const coinSparkleLeft = resourceManager.createObject("sprite", "coinSparkleLeft", this._coinLeft, {
        anchor: 0.5,
        fileName: "gem_sparkle.png",
        x: 60,
        y: -150,
        scale: 6,
      });
      const coinSparkleRight = resourceManager.createObject("sprite", "coinSparkleRight", this._coinRight, {
        anchor: 0.5,
        fileName: "gem_sparkle.png",
        x: 60,
        y: -150,
        scale: 6,
      });
      gsap.fromTo(
        [coinSparkleLeft, coinSparkleRight],
        {
          rotation: 0,
        },
        {
          duration: 1.4,
          rotation: Math.PI * 2,
          repeat: -1,
          ease: "none",
        }
      );

      const duration = 1.1;
      const delay = 1.12;
      this._coinLeftTimeline = gsap
        .timeline({ paused: true })
        .to(this._coinLeft, {
          duration: duration * 0.4,
          delay,
          pixi: { scale: this._coinScale * 2.5 },
          ease: "power2.out",
          onStart: () => {
            this._coinLeft.visible = true;
            this._coinLeft.x = this._coinLeftPos[0].x;
            this._coinLeft.y = this._coinLeftPos[0].y;
            this._coinLeft.scale.set(this._coinScale);
            coinSparkleLeft.alpha = 0;
          },
          onComplete: () => {
            gsap.to(this._coinLeft, {
              duration: duration * 0.6,
              pixi: { scale: 0 },
              ease: "power1.inOut",
            });
          },
        })
        .to(
          this._coinLeft,
          {
            duration,
            x: this._coinLeftPos[1].x,
            y: this._coinLeftPos[1].y,
            ease: "slow(0.5, 0.7, false)",
            onComplete: () => {
              this._coinLeft.visible = false;
            },
          },
          "<"
        )
        .to(
          coinSparkleLeft,
          {
            duration: duration * 0.4,
            alpha: 1,
            ease: "power1.inOut",
            repeat: 1,
            yoyo: true,
          },
          "<"
        );
      this._coinRightTimeline = gsap
        .timeline({ paused: true })
        .to(this._coinRight, {
          duration: duration * 0.4,
          delay,
          pixi: { scale: this._coinScale * 2.5 },
          ease: "power2.out",
          onStart: () => {
            this._coinRight.visible = true;
            this._coinRight.x = this._coinRightPos[0].x;
            this._coinRight.y = this._coinRightPos[0].y;
            this._coinRight.scale.set(this._coinScale);
            coinSparkleRight.alpha = 0;
          },
          onComplete: () => {
            gsap.to(this._coinRight, {
              duration: duration * 0.6,
              pixi: { scale: 0 },
              ease: "power1.inOut",
            });
          },
        })
        .to(
          this._coinRight,
          {
            duration,
            x: this._coinRightPos[1].x,
            y: this._coinRightPos[1].y,
            ease: "slow(0.5, 0.7, false)",
            onComplete: () => {
              this._coinRight.visible = false;
            },
          },
          "<"
        )
        .to(
          coinSparkleRight,
          {
            duration: duration * 0.4,
            alpha: 1,
            ease: "power1.inOut",
            repeat: 1,
            yoyo: true,
          },
          "<"
        );

      this._devilCharacterSpine.mixAnims("idle", "collect", 0.3);
      this._devilCharacterSpine.mixAnims("idle_spin", "collect", 0.3);
      this._devilCharacterSpine.mixAnims("collect", "idle", 0.8);
      this._devilCharacterSpine.mixAnims("collect", "idle_spin", 0.8);
      this._devilCharacterSpine.mixAnims("idle", "defensive", 0.3);
      this._devilCharacterSpine.mixAnims("idle_spin", "defensive", 0.3);
      this._devilCharacterSpine.mixAnims("idle", "add_both", 0.3);
      this._devilCharacterSpine.mixAnims("idle_spin", "add_both", 0.3);
      this._devilCharacterSpine.mixAnims("idle", "add_left", 0.3);
      this._devilCharacterSpine.mixAnims("idle_spin", "add_left", 0.3);
      this._devilCharacterSpine.mixAnims("idle", "add_right", 0.3);
      this._devilCharacterSpine.mixAnims("idle_spin", "add_right", 0.3);
      this._devilCharacterSpine.mixAnims("idle", "blow_bubble_multiplier", 0.3);
      this._devilCharacterSpine.mixAnims("idle_spin", "blow_bubble_multiplier", 0.3);
      this._devilCharacterSpine.mixAnims("blow_bubble_multiplier", "idle", 0.3);
      this._devilCharacterSpine.mixAnims("blow_bubble_multiplier", "idle_spin", 0.3);
      this._devilCharacterSpine.mixAnims("blow_bubble_multiplier", "collect", 0.3);
      this._devilCharacterSpine.mixAnims("idle", "lock_respin", 0.3);
      this._devilCharacterSpine.mixAnims("lock_respin", "idle", 0.3);

      this._chestContainer = resourceManager.get("chestsContainer");
      this._devilCharacterSpine.visible = false;
      this.devilVisible = false;
      this.currentBg = this._standardBGMovie;

      this._symbolMap = new Map([
        [
          gameController.standardCollectSymbol,
          { key: "standard", color: gameController.COLOR_TYPES.normal, bg: this._standardBGMovie },
        ],
        [
          gameController.boostCollectSymbol,
          { key: "boost", color: gameController.COLOR_TYPES.green, bg: this._boostBGMovie },
        ],
        [
          gameController.multiplierCollectSymbol,
          { key: "multi", color: gameController.COLOR_TYPES.blue, bg: this._multiBGMovie },
        ],
        [
          gameController.respinCollectSymbol,
          { key: "respin", color: gameController.COLOR_TYPES.red, bg: this._respinBGMovie },
        ],
      ]);
    }

    /**
     * Get background by key
     */
    getBackgroundByKey(key) {
      for (const value of this._symbolMap.values()) {
        if (value.key === key) {
          return value.bg;
        }
      }
      return null;
    }

    /**
     * Create the background spines
     */
    createBackgroundSprites() {
      // background movies
      this._standardBGMovie = resourceManager.createObject("movie", "standardBGMovie", this._devilContainer, {
        definition: "devilCollectBackground",
        anchor: 0.5,
        y: -186,
        loop: true,
      });
      // create a mask to remove the horns underneath the anim
      this._standardBGMovieMask = new PIXI.Graphics();
      this._standardBGMovieMask.beginFill(0xff0000);
      this._standardBGMovieMask.drawRect(-85, -111, 168, 197);
      this._standardBGMovieMask.endFill();
      this._standardBGMovie.addChild(this._standardBGMovieMask);
      this._standardBGMovie.mask = this._standardBGMovieMask;

      this._boostBGMovie = resourceManager.createObject("movie", "boostBGMovie", this._devilContainer, {
        definition: "devilBoostBackground",
        anchor: 0.5,
        y: -186,
        loop: true,
      });
      // create a mask to remove the horns underneath the anim
      this._boostBGMovieMask = new PIXI.Graphics();
      this._boostBGMovieMask.beginFill(0xff0000);
      this._boostBGMovieMask.drawRect(-85, -111, 168, 197);
      this._boostBGMovieMask.endFill();
      this._boostBGMovie.addChild(this._boostBGMovieMask);
      this._boostBGMovie.mask = this._boostBGMovieMask;

      this._multiBGMovie = resourceManager.createObject("movie", "multiBGMovie", this._devilContainer, {
        definition: "devilMultiplierBackground",
        anchor: 0.5,
        y: -186,
        loop: true,
      });
      // create a mask to remove the horns underneath the anim
      this._multiBGMovieMask = new PIXI.Graphics();
      this._multiBGMovieMask.beginFill(0xff0000);
      this._multiBGMovieMask.drawRect(-85, -111, 168, 197);
      this._multiBGMovieMask.endFill();
      this._multiBGMovie.addChild(this._multiBGMovieMask);
      this._multiBGMovie.mask = this._multiBGMovieMask;

      this._respinBGMovie = resourceManager.createObject("movie", "respinBGMovie", this._devilContainer, {
        definition: "devilRespinBackground",
        anchor: 0.5,
        y: -186,
        loop: true,
      });
      // create a mask to remove the horns underneath the anim
      this._respinBGMovieMask = new PIXI.Graphics();
      this._respinBGMovieMask.beginFill(0xff0000);
      this._respinBGMovieMask.drawRect(-85, -111, 168, 197);
      this._respinBGMovieMask.endFill();
      this._respinBGMovie.addChild(this._respinBGMovieMask);
      this._respinBGMovie.mask = this._respinBGMovieMask;

      this._standardBGMovie.visible = false;
      this._boostBGMovie.visible = false;
      this._multiBGMovie.visible = false;
      this._respinBGMovie.visible = false;
      this._standardBGMovie.gotoAndStop(0);
      this._boostBGMovie.gotoAndStop(0);
      this._multiBGMovie.gotoAndStop(0);
      this._respinBGMovie.gotoAndStop(0);

      this._modifierBackgrounds = {
        standard: this._standardBGMovie,
        boost: this._boostBGMovie,
        multi: this._multiBGMovie,
        respin: this._respinBGMovie,
      };
    }

    /**
     * Grab the devil character spine assets
     */
    _onLayoutComplete() {}

    showAnimatedBackground() {
      let reelSymbol;

      const isBuyABonusFirstSpin = gameController.inBuyABonus && gameController.inBuyABonusFirstSpin;

      if (isBuyABonusFirstSpin || !gameController.convertedReelResults) {
        reelSymbol = gameController.standardCollectSymbol;
      } else {
        reelSymbol = gameController.convertedReelResults[gameController.devilReel][0];
      }

      this._matchSmb = this._symbolMap.get(reelSymbol);

      if (!this._matchSmb) return;

      if (gameController.inFreeSpins && !isBuyABonusFirstSpin) {
        if (this.currentBg !== this._matchSmb.bg) {
          this._fadeOutCurrentBgTween = gsap.fromTo(
            this.currentBg,
            { alpha: 1 },
            {
              alpha: 0,
              duration: 1,
              onComplete: () => {
                this.currentBg.visible = false;
                this._fadeOutCurrentBgTween = null;
              },
            }
          );

          const centreStatic = resourceManager.get("slotContainer:reelContainer:reel7");
          centreStatic.setReelSymbol(0, reelSymbol);
          this._staticFadeIn = gsap.fromTo(centreStatic.children[0], { alpha: 0 }, { alpha: 1, duration: 1 });

          this._matchSmb.bg.play();
          this._matchSmb.bg.visible = true;
          gameController.reelBorderFire.switchColour(this._matchSmb.color, 1);

          this._fadeInNewBgTween = gsap.fromTo(
            this._matchSmb.bg,
            { alpha: 0 },
            {
              alpha: 1,
              duration: 1,
              onComplete: () => {
                this.currentBg = this._matchSmb.bg;
                this._fadeInNewBgTween = null;
              },
            }
          );
        } else {
          this._currentModiferBackgroundShowing = this._matchSmb.key;
          this.currentBg.alpha = 1;
          this.currentBg.play();
          this.currentBg.visible = true;
        }
      } else {
        this.currentBg = this._matchSmb.bg;
        this._currentModiferBackgroundShowing = this._matchSmb.key;
        this.currentBg.alpha = 1;
        this.currentBg.play();
        this.currentBg.visible = true;
      }
    }

    slamStop() {
      if (this._fadeOutCurrentBgTween) {
        this._fadeOutCurrentBgTween.progress(1);
        this._fadeInNewBgTween.progress(1);
        gameController.reelBorderFire.slamStop();
      }
    }

    /**
     * Show the centre devil
     */
    appear() {
      this.showAnimatedBackground();
      this._devilCharacterSpine.visible = true;

      const duration = 0.25;

      gsap.fromTo(
        this._devilCharacterSpine.scale,
        {
          x: this._devilScale / 2,
          y: this._devilScale / 2,
        },
        {
          duration,
          x: this._devilScale,
          y: this._devilScale,
        }
      );
      this.devilVisible = true;

      gsap.to(this._blackSquare, {
        duration,
        alpha: 1,
      });
    }

    /**
     * Hide the centre devil
     */
    disappear(inFreeSpins) {
      if (!inFreeSpins) {
        this._devilCharacterSpine.visible = false;
        this.devilVisible = false;
        this.currentBg.visible = false;
        this.currentBg.gotoAndStop(0);
        this._blackSquare.alpha = 0;
      }
    }

    /**
     * Play respinIdle anim
     */
    lockRespin(cb) {
      gsap.delayedCall(0.6, () => {
        soundManager.play("lock");
      });

      this._devilCharacterSpine.spineObject.state.clearListeners();
      this._devilCharacterSpine.play("lock_respin", false, 0, () => {
        if (cb) {
          cb();
        }
      });
    }

    /**
     * Alternate through the unlocked bonus colours
     */
    alternateModifierBackgrounds(currentUnlockedModifiers) {
      if (this._cycleOutTween) {
        this._cycleOutTween.kill();
        this._cycleOutTween = null;
      }
      if (this._cycleInTween) {
        this._cycleInTween.kill();
        this._cycleOutTween = null;
      }

      this._cyclingBackground = true;
      this._cycleBackgroundColours(["standard"].concat(currentUnlockedModifiers));
    }

    /**
     * Cycle background colours
     */
    async _cycleBackgroundColours(unlockedModifiers) {
      if (this._cyclingBackground) {
        this._cycleBackgroundIndex =
          (unlockedModifiers.indexOf(this._currentModiferBackgroundShowing) + 1) % unlockedModifiers.length;

        // Get current and previous modifier
        const cycleInModifier = unlockedModifiers[this._cycleBackgroundIndex];

        const cycleInBackground = this._cycleInLandingSymbol
          ? this.getBackgroundByKey(this._cycleInNextBackground)
          : this.getBackgroundByKey(cycleInModifier);
        const cycleOutBackground = this.getBackgroundByKey(this._currentModiferBackgroundShowing);

        if (cycleInBackground !== cycleOutBackground) {
          this._cycleOutTween = gsap.fromTo(cycleOutBackground, { alpha: 1 }, { alpha: 0, duration: 0.3 });
          this._cycleOutTween.then(() => cycleOutBackground.gotoAndStop(0));

          cycleInBackground.play();
          this._cycleInTween = gsap.fromTo(cycleInBackground, { alpha: 0 }, { alpha: 1, duration: 0.3 });
          cycleInBackground.visible = true;

          this._currentModiferBackgroundShowing = this._cycleInLandingSymbol
            ? this._cycleInNextBackground
            : cycleInModifier;
        }

        // Wait before cycling again
        await new Promise((res) => gsap.delayedCall(1, res));

        this._cycleBackgroundColours(unlockedModifiers);

        if (this._cycleInLandingSymbol) {
          this._cyclingBackground = false;
          this._cycleInLandingSymbol = false;
        }
      }
    }

    /**
     * Handle centre devil, check if collecting any coins, play appt animations
     */
    async handleCentreDevil() {
      if (!this.devilVisible) {
        this.appear();
      }

      const hasCoins = gameController.checkForCoinCollect();

      if (hasCoins) {
        if (gameController.hasRespun) {
          musicManager.onRespinsEnd();
        }
        this.collecting();
        await CoinsView.doCashCoins();
      }
    }

    /**
     * When the Kraken throws a coin, resolve after delay
     * rather than animation end so it syncs with chest shake
     */
    playDevilAnim(name, delay = 1400) {
      return new Promise((resolve) => {
        this._devilCharacterSpine.play(name, false, 0, () => {
          this.idleLoop();
        });

        setTimeout(resolve, delay);
      });
    }

    /**
     * Play left chest add anim
     */
    async leftChest() {
      if (!this.devilVisible) {
        this.appear();
      }
      soundManager.play("devilFlamePoint");

      this._coinLeftTimeline.play(0);

      gsap.delayedCall(this._coinFlipSoundDelay, () => {
        soundManager.play("coinFlip");
      });

      await this.playDevilAnim("add_left");
      await new Promise((res) => gsap.delayedCall(0.6, res));
    }

    /**
     * Play right chest add anim
     */
    async rightChest() {
      if (!this.devilVisible) {
        this.appear();
      }
      soundManager.play("devilFlamePoint");

      this._coinRightTimeline.play(0);

      gsap.delayedCall(this._coinFlipSoundDelay, () => {
        soundManager.play("coinFlip");
      });

      await this.playDevilAnim("add_right");
      await new Promise((res) => gsap.delayedCall(0.6, res));
    }

    /**
     * Play both chest add anim
     */
    async bothChests() {
      if (!this.devilVisible) {
        this.appear();
      }

      soundManager.play("devilFlamePoint");

      this._coinLeftTimeline.play(0);
      this._coinRightTimeline.play(0);

      gsap.delayedCall(this._coinFlipSoundDelay, () => {
        soundManager.play("coinFlipMulti");
      });

      await this.playDevilAnim("add_both");
      await new Promise((res) => gsap.delayedCall(0.6, res));
    }

    /**
     * Play collecting anim
     */
    async collecting() {
      if (!this.devilVisible) {
        this.appear();
      }
      musicManager.playDevilPositiveSound();
      await this._devilCharacterSpine.play("collect", true);
    }

    /**
     * Play respinIdle anim
     */
    respinIdle(loop = false) {
      this._devilCharacterSpine.play("idle_spin", loop);
    }

    /**
     * Play fireball intro anim
     */
    fireBallIntro() {
      this._devilCharacterSpine.play("blow_bubble_multiplier", true);
      gsap.delayedCall(0.4, () => {
        soundManager.play("orbAppear");
      });
    }

    /**
     * Play frustrated anim
     */
    frustrated() {
      if (!this.devilVisible) {
        this.appear();
      }

      return new Promise((res) => {
        musicManager.playDevilNegativeSound();
        this._devilCharacterSpine.play("defensive", false, 0, res);
      });
    }

    /**
     * Play idle loop anim
     */
    idleLoop() {
      if (!this.devilVisible) {
        this.appear();
      }
      this._devilCharacterSpine.play("idle", false, 0, () => {
        this._idleLoopCounter++;
        if (this._idleLoopCounter >= 2) {
          this.idleBlink();
        } else {
          this.idleLoop();
        }
      });
    }

    idleBlink() {
      this._idleLoopCounter = 0;
      this._devilCharacterSpine.play("idle_spin", false, 0, () => this.idleLoop());
    }

    /**
     * Respin intro
     */
    respinIntro() {
      return new Promise((resolve) => {
        this.lockRespin(() => {
          musicManager.playDevilLaughSound();
          musicManager.onRespinsStart();
          this.respinIdle(true);

          resolve();
        });
      });
    }

    /**
     * Handle orientation change
     * @param {boolean} isLandscape
     */
    _orientationChange(isLandscape) {}
  }

  resourceManager.registerObjectType(
    "centreDevil",
    () => new CentreDevil(),
    () => {},
    () => []
  );
})();
